<script lang="ts">
  import IntegrationTypes from "../constants/IntegrationTypes"
  import {
    buildUrlData,
    type BuildUrlDataOutput,
  } from "../lib/buildModuleUrlData"
  import { getContext } from "svelte"
  import Svg from "../components/Svg.svelte"
  import closeIcon from "../Icons/close_banner.svg?raw"
  import carIcon from "../Icons/car.svg?raw"
  import { createEventDispatcher } from "svelte"
  import { onMount } from "svelte"
  import { getTestDriveModuleType } from "website_script/lib/getTestDriveModuleType"
  import type { Theme } from "website_script/types/theme"
  import type { Texts } from "website_script/types/texts"
  import type { Settings } from "website_script/types/settings"
  import type { Environment } from "website_script/types/environment"
  import { get } from "svelte/store"

  /** Trigger for the root modal */
  export let onModalTrigger: (urlBuildData: BuildUrlDataOutput) => void

  const { trackFunction, abTesting } = getContext<Environment>("environment")
  const { testDriveBannerText, testDriveBannerCta } = getContext<Texts>("texts")
  const {
    enableFixedTestDriveBanner,
    selectorConflictingWithTestDriveBanner,
    enableTestDriveBanner,
    enableTestDriveCtas,
  } = getContext<Settings>("settings")
  const settings = getContext<Settings>("settings")
  const { accentColor, textCtaColor, neutralTextColor } =
    getContext<Theme>("theme")

  $: testDriveModuleType = getTestDriveModuleType(settings)

  const urlBuildData = buildUrlData({
    settings,
    abTesting: get(abTesting),
    moduleType: testDriveModuleType,
    integrationType: IntegrationTypes.TEST_DRIVE_BANNER,
    // TODO: FIX - We are not checking for any department here, so the banner always opens for customer!
    departmentUuid: null,
  })

  let elementConflictingWithBanner: HTMLElement | null

  onMount(() => {
    setTimeout(() => {
      trackFunction(
        `Test Drive Banner Shown`,
        window.trackingConstants.eventTypeConstants.DISPLAYED_CTA,
        window.trackingConstants.eventProductConstants.TEST_DRIVE
      )
    }, 50)

    if (selectorConflictingWithTestDriveBanner) {
      elementConflictingWithBanner = document.querySelector(
        selectorConflictingWithTestDriveBanner
      )
    }

    if (elementConflictingWithBanner) {
      elementConflictingWithBanner.style.marginTop = "50px"
    }
  })

  /**
   * Opens Modal on click based on url data
   */
  const clickHandler = () => {
    onModalTrigger(urlBuildData)

    // Tracks when banner is clicked
    trackFunction(
      `Clicked on Test Drive Banner`,
      window.trackingConstants.eventTypeConstants.INTERACTED_WITH_CTA,
      window.trackingConstants.eventProductConstants.TEST_DRIVE
    )
  }

  const dispatch = createEventDispatcher()
  /**
   * Closes the Test-Drive-Banner via dispatching the close event
   */
  const closeHandler = () => {
    dispatch("close")
    if (elementConflictingWithBanner) {
      elementConflictingWithBanner.style.marginTop = "0px"
    }
    // Tracks when banner is closed
    trackFunction(
      `Closed Test Drive Banner`,
      window.trackingConstants.eventTypeConstants.INTERACTED_WITH_CTA,
      window.trackingConstants.eventProductConstants.TEST_DRIVE
    )
  }

  /**
   * Returns the first Element within the document that matches
   * the specified selector
   */
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
{#if enableTestDriveBanner && enableTestDriveCtas}
  <div
    class={`auws-td-banner-container`}
    style={`position: ${enableFixedTestDriveBanner ? "fixed" : "absolute"}; ${
      enableFixedTestDriveBanner && "padding-right: 50px"
    }; --button-bg-color: ${accentColor}; --button-text-color: ${textCtaColor}; --neutral-text-color: ${neutralTextColor}`}
  >
    {#if enableFixedTestDriveBanner}
      <div class={`auws-td-banner-close`} on:click={closeHandler}>
        <Svg data={closeIcon} alt="close the banner" size="16px" />
      </div>
    {/if}
    <p class={`auws-td-banner-text`}>
      {@html testDriveBannerText}
    </p>
    <div class={`auws-td-banner-button`} on:click={clickHandler}>
      <Svg data={carIcon} alt="book her" size="24px" />
      {@html testDriveBannerCta}
    </div>
  </div>
{/if}

<style>
  .auws-td-banner-container {
    top: 0;
    left: 0;
    opacity: 0;
    width: 100%;
    box-sizing: border-box;
    height: 50px;
    display: flex;
    justify-content: center;
    align-items: center;
    background: #f1f2f4;
    color: var(--neutral-text-color);
    opacity: 1;
    z-index: 20000000;
  }
  .auws-td-banner-text {
    margin: 0px !important;
    padding: 0px !important;
    font-size: 14px;
    line-height: 17px;
  }
  .auws-td-banner-button {
    box-sizing: border-box;
    background: var(--button-bg-color);
    padding: 6px 12px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen,
      Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
    font-size: 13px;
    color: var(--button-text-color);
    border-radius: 4px;
    cursor: pointer;
    max-height: 32px;
    margin-left: 40px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .auws-td-banner-close {
    position: absolute;
    right: 20px;
    top: 50%;
    transform: translateY(-50%);
    margin: 0;
    padding: 0 !important;
    height: 16px;
    cursor: pointer;
  }

  @media screen and (max-width: 760px) {
    .auws-td-banner-container {
      height: auto;
      padding: 16px;
    }

    .auws-td-banner-button {
      padding: 6px;
      margin-left: 5px;
      min-width: 130px;
    }

    .auws-td-banner-text {
      font-size: 13px;
    }
  }
</style>
